import { motion } from 'framer-motion';
import { Col } from 'reactstrap';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify'; 
import { useDispatch } from 'react-redux';
import { cartActions } from '../../redux/slices/cartSlice';
import '../../styles/product-card.css';
import "react-toastify/dist/ReactToastify.css";
import { useEffect, useRef, useState } from 'react';

const ProductCard = ({ item, wrapWithCol = true, activeCard, setActiveCard }) => {
    const dispatch = useDispatch();
    const [isHovered, setIsHovered] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const cardRef = useRef(null);

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 1020);
        };

        // Check initially
        checkMobile();

        // Add resize listener
        window.addEventListener('resize', checkMobile);
        
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        if (!isMobile) return;

        const handleTouchOutside = (e) => {
            if (cardRef.current && !cardRef.current.contains(e.target)) {
                setActiveCard(null);
            }
        };

        document.addEventListener('touchstart', handleTouchOutside);
        return () => document.removeEventListener('touchstart', handleTouchOutside);
    }, [isMobile, setActiveCard]);

    const getIsOutOfStock = (item) => {
        if (!item.variations || item.variations.length === 0) return true;
        return item.variations.every(variation =>
            (!variation.sizes || variation.sizes.length === 0) ||
            variation.sizes.every(size => !size.quantity || size.quantity < 1)
        );
    };

    const addToCart = () => {
        const variation = item.variations?.[0];

        const isSingleDefaultVariation =
            item.variations?.length === 1 &&
            (variation.variationName === 'default' || variation.variationName === '');

        const isOutOfStock = isSingleDefaultVariation
            ? variation.sizes?.every(size => size.qty <= 0)
            : true;

        if (isOutOfStock) {
            toast.error("Out of Stock", {
                position: "top-right",
                autoClose: 1000,
                hideProgressBar: true,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                closeButton: false,
                className: "out-of-stock-toast",
            });
            return;
      }

      const selectedSize = variation.sizes?.find(size => size.quantity > 0);

      const product = {
          itemId: `${item.id}-${item.productName}-${variation?.variationName?.toLowerCase() || ''}`,
          id: item.id,
          productName: item.productName,
          productSize: item.productSize,
          weight: item?.weight,
          price: selectedSize?.price || item.price,
          imgUrl: variation?.imgUrl || item.imgUrl[0],
          size: selectedSize?.size,
          color: variation?.color,
          variationName: variation?.variationName || '',
          quantity: 1,
          stockQty: selectedSize?.quantity
      };

      dispatch(cartActions.addItem(product));
    };

    const handleTouch = (e) => {
        if (!isMobile) return;
        e.preventDefault();
        e.stopPropagation();
        
        // Toggle active state - if already active, deactivate, otherwise activate
        if (activeCard === item.id) {
            setActiveCard(null);
        } else {
            setActiveCard(item.id);
        }
    };

    const handleMouseEnter = () => {
        if (!isMobile) {
            setIsHovered(true);
        }
    };

    const handleMouseLeave = () => {
        if (!isMobile) {
            setIsHovered(false);
        }
    };

    const isActive = isMobile ? activeCard === item.id : isHovered;


    const content = (
        <div className={`product-item ${!wrapWithCol ? 'grotto-style-card' : ''}`} ref={cardRef}>
            <Link to={`/productDetails/${item.id}`}>
                <div
                    className="product-image-container"
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    onTouchStart={handleTouch}
                >
                    <motion.img
                        key="main-image"
                        src={item.imgUrl[0]}
                        alt={item.productName}
                        className="product-image main-image"
                        initial={{ opacity: 1 }}
                        animate={{ opacity: isActive && item.imgUrl[1] ? 0 : 1 }}
                        transition={{ duration: 0.4 }}
                    />
                    {item.imgUrl[1] && (
                        <motion.img
                            key="hover-image"
                            src={item.imgUrl[1]}
                            alt={`${item.productName} alt`}
                            className="product-image hover-image"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: isActive ? 1 : 0 }}
                            transition={{ duration: 0.4 }}
                        />
                    )}

                </div>
            </Link>
            <div className='product-info'>
                <h3 className='product-name'>
                    <Link to={`/productDetails/${item.id}`}>
                        {item.productName.toLowerCase().replace(/\b\w/g, s => s.toUpperCase())}
                    </Link>
                </h3>
                {getIsOutOfStock(item) && (
                    <span className="out-of-stock-btn">Sold Out</span>
                )}
            </div>
            <div className='product_card-bottom d-flex align-items-center justify-content-between'>
                <span className='price'>${item.price}</span>
                {item.variations &&
                    item.variations.length === 1 &&
                        (item.variations[0].variationName === 'default' || item.variations[0].variationName === '') &&
                            item.variations[0].sizes?.length === 1 && item.variations[0].sizes[0]?.size === "" &&
                                item.variations[0].sizes?.[0]?.quantity > 0 && (
                                    <motion.span role='button' whileTap={{ scale: 1.2 }} onClick={addToCart}>
                                        <i className="ri-add-line"></i>
                                    </motion.span>
                )}
            </div>
        </div>
    );

    return wrapWithCol ? (
        <Col className='col-6' lg={3} md={6} sm={6}>
          {content}
        </Col>
    ) : (
        content
    );
};

export default ProductCard;
